import React, {Suspense} from 'react';
import {Chip, IconButton} from "@mui/material";
import menus from "./menus";
import JumboVerticalNavbar from "@jumbo/components/JumboVerticalNavbar/JumboVerticalNavbar";
import {DrawerHeader} from "@jumbo/components/JumboLayout/style";
import JumboScrollbar from "@jumbo/components/JumboScrollbar";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import useJumboSidebarTheme from "@jumbo/hooks/useJumboSidebarTheme";
import {SIDEBAR_STYLES, SIDEBAR_VIEWS} from "@jumbo/utils/constants/layout";
import Logo from "../../../../shared/Logo";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import Zoom from "@mui/material/Zoom";
import Div from "@jumbo/shared/Div";
import SidebarSkeleton from "./SidebarSkeleton";
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { APP_VERSION, ENV_NAME, HOST } from 'app/utils/constants/paths';
import {Typography} from "@mui/material";
import {useQuery} from "react-query";
import versionServices from 'app/services/version-services';
import _ from 'lodash';

const Sidebar = () => {
    const {authUser, isLoading} = useJumboAuth();
    const {sidebarOptions} = useJumboLayoutSidebar();

    // Con el sidebar reducido no hay ancho para el texto: mismo criterio que usa
    // el header para decidir si muestra el logo chico.
    const isMiniAndClosed = React.useMemo(() => {
        return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
    }, [sidebarOptions.view, sidebarOptions.open]);

    // El front trae su version en el bundle; la del backend hay que preguntarla.
    // Van las dos porque no avanzan juntas: se despliegan por caminos distintos y
    // una puede haber quedado atras. No se reintenta ni se refresca, que solo
    // cambian con un deploy y ese deploy recarga la pagina.
    const {data: backendVersion} = useQuery('system-version', versionServices.getBackendVersion, {
        staleTime: Infinity,
        retry: false,
        refetchOnWindowFocus: false,
    });

    return (
        <React.Fragment>
            <SidebarHeader/>
            <JumboScrollbar
                autoHide
                autoHideDuration={200}
                autoHideTimeout={500}
            >
                {(!authUser || isLoading) ? (
                    <Div
                        sx={{
                            display: 'flex',
                            minWidth: 0,
                            alignItems: 'center',
                            alignContent: 'center',
                            px: 3
                        }}
                    >
                        <SidebarSkeleton/>
                    </Div>
                ) : (
                    <JumboVerticalNavbar translate items={menus(authUser)}/>
                )}
                { ENV_NAME !== 'production' &&
                    <Div sx={{px: 2}}>
                        <Chip label={_.upperCase(ENV_NAME)} color="secondary" sx={{ width: '100%' }}/>
                    </Div>
                }
                { !isMiniAndClosed &&
                    <Div sx={{px: 2, pt: 1.5, pb: 2, textAlign: 'center'}}>
                        <Typography variant={"body2"} color="text.secondary"
                                    sx={{fontFamily: 'monospace', fontSize: 11, lineHeight: 1.7}}>
                            front {APP_VERSION}
                        </Typography>
                        <Typography variant={"body2"} color="text.secondary"
                                    sx={{fontFamily: 'monospace', fontSize: 11, lineHeight: 1.7}}>
                            back {backendVersion ?? '\u2026'}
                        </Typography>
                    </Div>
                }
            </JumboScrollbar>
        </React.Fragment>
    );
};

const SidebarHeader = () => {
    const {sidebarOptions, setSidebarOptions} = useJumboLayoutSidebar();
    const {sidebarTheme} = useJumboSidebarTheme();

    const {authUser} = useJumboAuth();
    const compLogo = authUser?.current_company?.logo_url;
    const compLogoUrl = compLogo ? `${HOST}${compLogo}` : null;

    const isMiniAndClosed = React.useMemo(() => {
        return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
    }, [sidebarOptions.view, sidebarOptions.open]);
    
    return (
        <React.Fragment>
            {
                sidebarOptions?.style !== SIDEBAR_STYLES.CLIPPED_UNDER_HEADER &&
                <DrawerHeader>
                    <Logo mini={isMiniAndClosed} mode={sidebarTheme.type} logo_url={compLogoUrl} link_sx={{display: 'flex'}} />
                    {
                        sidebarOptions?.view !== SIDEBAR_VIEWS.MINI &&
                        <Zoom in={sidebarOptions?.open}>
                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                sx={{ml: 0, mr: -1.5}}
                                onClick={() => setSidebarOptions({open: false})}
                            >
                                <MenuOpenIcon/>
                            </IconButton>
                        </Zoom>
                    }
                </DrawerHeader>
            }
        </React.Fragment>
    )
};

export default Sidebar;
